import { type ReactElement, type ReactNode, useEffect, useRef, useState } from 'react';
import { EditAreaDraftContext } from './edit-area-draft-context';
import { createEditAreaDraftStore } from './edit-area-draft-store';
import type { EditAreaDraftOptions } from './edit-area-draft-types';
import { PhotoDraftContext } from './photo-draft-context';

interface EditAreaDraftProviderProps extends EditAreaDraftOptions {
  readonly children: ReactNode;
}

/** 두 context는 동일 store를 제공한다. serverState prop 재조회는 본문을 초기화하지 않고, measurement prop 변경은 실측만 교체한다. */
export const EditAreaDraftProvider = ({
  children,
  ...options
}: EditAreaDraftProviderProps): ReactElement => {
  const callbacks = useRef(options);
  callbacks.current = options;
  const [store] = useState(() =>
    createEditAreaDraftStore({
      ...options,
      onPhotosChange: (draft) => callbacks.current.onPhotosChange?.(draft),
      onPhotoReplaced: (event) => callbacks.current.onPhotoReplaced?.(event),
    }),
  );
  useEffect(() => {
    store.getState().reconcile(options.colorOptionIds, options.serverState?.viewing.definition);
  }, [store, options.colorOptionIds, options.serverState]);
  const widthMm = options.measurement?.widthMm ?? null;
  const heightMm = options.measurement?.heightMm ?? null;
  useEffect(() => {
    store
      .getState()
      .setMeasurement(widthMm === null || heightMm === null ? null : { widthMm, heightMm });
  }, [store, widthMm, heightMm]);
  return (
    <EditAreaDraftContext.Provider value={store}>
      <PhotoDraftContext.Provider value={store}>{children}</PhotoDraftContext.Provider>
    </EditAreaDraftContext.Provider>
  );
};
